import type {QueryClient} from '@github-ui/react-query'
import type {
  AgentMergeActivityNotification,
  AgentMergeStatusResponse,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {AgentMergeStatusResponse_Selection as Selection} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {
  hydrateActivity,
  initialActivityState,
  invalidateActivity,
  nextActivityDeadline,
  presentActivity,
  receiveActivity,
  retainedActivityRecords,
  type ActivityAnnouncementSource,
  type ActivityPresentation,
} from './agent-merge-activity-reducer'

const RECORD_LIMIT = 4096
const SESSION_ANNOUNCEMENT_LIMIT = 256
type Registry = {
  records: number
  disabled: boolean
  stores: Map<string, AgentMergeActivityStore>
  sessionAnnouncements: Set<string>
}
const registries = new WeakMap<QueryClient, Registry>()
const SERVER_SNAPSHOT: ActivityPresentation = {row: 'baseline'}

export class AgentMergeActivityStore {
  state = initialActivityState()
  recovering = true
  disabled = false
  #registry: Registry
  #key: string
  #listeners = new Set<() => void>()
  #timer: ReturnType<typeof setTimeout> | undefined
  #snapshot = SERVER_SNAPSHOT
  #contextKey = ''
  #announcements = new Set<string>()

  constructor(registry: Registry, key: string) {
    this.#registry = registry
    this.#key = key
  }

  subscribe = (listener: () => void) => {
    this.#listeners.add(listener)
    this.tick()
    return () => {
      this.#listeners.delete(listener)
      if (!this.#listeners.size) {
        clearTimeout(this.#timer)
        this.state = invalidateActivity(this.state)
        this.recovering = true
      }
    }
  }

  getSnapshot = () => this.#snapshot
  getServerSnapshot = () => SERVER_SNAPSHOT

  configure(key: string) {
    if (this.#contextKey === key) return
    this.#contextKey = key
    this.reconcile()
  }

  reconcile = () => {
    this.recovering = true
    this.state = invalidateActivity(this.state)
    this.tick()
  }

  claimAnnouncement = (key: string, source: ActivityAnnouncementSource = 'session') => {
    if (source !== 'activity') {
      const claims = this.#registry.sessionAnnouncements
      const scopedKey = JSON.stringify([this.#key, key])
      const alreadyClaimed = claims.delete(scopedKey)
      claims.add(scopedKey)
      if (claims.size > SESSION_ANNOUNCEMENT_LIMIT) {
        const oldest = claims.values().next().value
        if (oldest !== undefined) claims.delete(oldest)
      }
      return !alreadyClaimed
    }
    if (this.#announcements.has(key) || this.#registry.disabled) return false
    if (this.#registry.records >= RECORD_LIMIT) {
      disableRegistry(this.#registry)
      return false
    }
    this.#announcements.add(key)
    this.#registry.records++
    return true
  }

  #commit(next: typeof this.state) {
    const added = retainedActivityRecords(next) - retainedActivityRecords(this.state)
    if (!this.disabled && added > 0 && this.#registry.records + added > RECORD_LIMIT) {
      disableRegistry(this.#registry)
    }
    if (this.disabled) {
      this.state = {...this.state, context: next.context, selection: next.selection, session: next.session}
    } else {
      this.#registry.records += added
      this.state = next
    }
    this.tick()
  }

  hydrate(response: AgentMergeStatusResponse) {
    this.recovering = false
    this.#commit(hydrateActivity(this.state, response))
  }

  receive(notification: AgentMergeActivityNotification) {
    if (this.disabled || !notification.activity) return
    this.#commit(receiveActivity(this.state, notification.scope, notification.activity))
  }

  unavailable = () => {
    this.state = {...this.state, session: undefined, selection: Selection.UNAVAILABLE}
    this.reconcile()
  }

  tick = () => {
    clearTimeout(this.#timer)
    const now = Date.now()
    const showActivity = !this.recovering && !this.disabled
    this.#snapshot = presentActivity(this.state, now, showActivity)
    for (const listener of this.#listeners) listener()
    const deadline = showActivity ? nextActivityDeadline(this.state, now) : Infinity
    if (this.#listeners.size && Number.isFinite(deadline)) {
      this.#timer = setTimeout(this.tick, Math.max(1, deadline - now))
    }
  }
}

function disableRegistry(registry: Registry) {
  if (registry.disabled) return
  registry.disabled = true
  setTimeout(() => {
    throw new Error('Agent Merge transient activity identity budget exhausted')
  })
  for (const store of registry.stores.values()) {
    store.disabled = true
    store.state = invalidateActivity(store.state)
    store.tick()
  }
}

export function agentMergeActivityStore(client: QueryClient, key: string) {
  let registry = registries.get(client)
  if (!registry) {
    registry = {records: 0, disabled: false, stores: new Map(), sessionAnnouncements: new Set()}
    registries.set(client, registry)
  }
  const existing = registry.stores.get(key)
  if (existing) return existing
  const store = new AgentMergeActivityStore(registry, key)
  if (registry.disabled || registry.records >= RECORD_LIMIT) {
    disableRegistry(registry)
    store.disabled = true
  } else {
    registry.records++
    registry.stores.set(key, store)
  }
  return store
}
